import React from 'react';
import { motion } from 'framer-motion';
import { Coffee, Sparkles, Users, Music, Camera, Heart, ArrowRight } from 'lucide-react';

const experienceSteps = [
  {
    icon: Coffee,
    title: 'Welcome & Arrival',
    description: 'Traditional welcome drinks and registration as you step into a world of Kerala culture',
    gradient: 'from-yellow-400 to-orange-400',
    bgGradient: 'from-yellow-50 via-orange-50 to-amber-50',
    number: '01',
    size: 'large'
  },
  {
    icon: Sparkles,
    title: 'Sacred Rituals',
    description: 'Experience the beauty of lamp lighting, garland exchange, and traditional blessings',
    gradient: 'from-emerald-400 to-teal-400',
    bgGradient: 'from-emerald-50 via-teal-50 to-cyan-50',
    number: '02',
    size: 'medium'
  },
  {
    icon: Users,
    title: 'Guest Participation',
    description: 'Join in interactive games and fun activities that bring everyone together',
    gradient: 'from-amber-400 to-yellow-400',
    bgGradient: 'from-amber-50 via-yellow-50 to-orange-50',
    number: '03',
    size: 'medium'
  },
  {
    icon: Heart,
    title: 'Grand Sadya Feast',
    description: 'Savor an authentic Kerala wedding feast served on banana leaf with 20+ delicacies',
    gradient: 'from-rose-400 to-pink-400',
    bgGradient: 'from-rose-50 via-pink-50 to-red-50',
    number: '04',
    size: 'large'
  },
  {
    icon: Music,
    title: 'Chenda Melam',
    description: 'Feel the rhythm of live traditional percussion that echoes through celebrations',
    gradient: 'from-blue-400 to-cyan-400',
    bgGradient: 'from-blue-50 via-cyan-50 to-sky-50',
    number: '05',
    size: 'medium'
  },
  {
    icon: Camera,
    title: 'Photos & Farewell',
    description: 'Create lasting memories at our curated photo zones with traditional backdrops',
    gradient: 'from-purple-400 to-indigo-400',
    bgGradient: 'from-purple-50 via-indigo-50 to-violet-50',
    number: '06',
    size: 'medium'
  }
];

const ExperienceSection = () => {
  return (
    <section className="py-24 md:py-32 px-6 bg-gradient-to-b from-white via-amber-50/30 to-white relative overflow-hidden" data-testid="experience-section">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-200 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-emerald-200 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="inline-block mb-4">
            <span className="px-6 py-2 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-900 text-sm font-bold rounded-full uppercase tracking-wider shadow-sm">
              Your Journey
            </span>
          </div>
          <h2 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-emerald-900 via-emerald-800 to-teal-900 bg-clip-text text-transparent mb-6" data-testid="experience-title">
            The Experience
          </h2>
          <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed" data-testid="experience-subtitle">
            Immerse yourself in an authentic Kerala wedding celebration—every moment crafted to perfection
          </p>
        </motion.div>

        {/* Bento Grid / Masonry Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16">
          {experienceSteps.map((step, index) => {
            const isLarge = step.size === 'large';

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`group relative ${isLarge ? 'md:col-span-2 lg:col-span-2' : 'md:col-span-1'}`}
                data-testid={`experience-step-${index}`}
              >
                <div className={`relative h-full bg-gradient-to-br ${step.bgGradient} rounded-3xl overflow-hidden border border-white/60 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2`}>
                  {/* Glassmorphism overlay */}
                  <div className="absolute inset-0 bg-white/40 backdrop-blur-sm"></div>

                  {/* Content */}
                  <div className={`relative z-10 p-8 md:p-10 ${isLarge ? 'min-h-[320px] flex flex-col justify-between' : 'min-h-[280px] flex flex-col justify-between'}`}>
                    {/* Top section */}
                    <div>
                      {/* Icon and number */}
                      <div className="flex items-start justify-between mb-6">
                        <div className={`p-4 rounded-2xl bg-gradient-to-br ${step.gradient} shadow-xl group-hover:scale-110 transition-transform duration-300`}>
                          <step.icon className="w-8 h-8 md:w-10 md:h-10 text-white" strokeWidth={2} />
                        </div>
                        <div className="text-right">
                          <span className={`text-6xl md:text-7xl font-bold bg-gradient-to-br ${step.gradient} bg-clip-text text-transparent opacity-30 group-hover:opacity-50 transition-opacity`}>
                            {step.number}
                          </span>
                        </div>
                      </div>

                      {/* Title and description */}
                      <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-emerald-900 mb-4 leading-tight">
                        {step.title}
                      </h3>
                      <p className="text-stone-700 text-base md:text-lg leading-relaxed">
                        {step.description}
                      </p>
                    </div>

                    {/* Bottom section - decorative line */}
                    <div className="mt-6 flex items-center gap-3 group-hover:gap-5 transition-all duration-300">
                      <div className={`h-1 flex-grow rounded-full bg-gradient-to-r ${step.gradient}`}></div>
                      <ArrowRight className={`w-5 h-5 text-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity`} />
                    </div>
                  </div>

                  {/* Decorative corner accent */}
                  <div className={`absolute -bottom-12 -right-12 w-48 h-48 bg-gradient-to-br ${step.gradient} rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition-opacity`}></div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-4 px-10 py-5 bg-gradient-to-r from-emerald-900 to-teal-800 rounded-full shadow-2xl">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></div>
              <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse delay-100"></div>
              <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse delay-200"></div>
            </div>
            <p className="text-white text-lg md:text-xl font-semibold">
              <span className="text-amber-300 font-bold">4-5 hours</span> of immersive cultural celebration
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExperienceSection;
